import apiEndPoints from "./apiEndPoints";
import { VISITOR_MOBILE_OTP_ENABLED } from "./constants";
import { apiCall } from "./httpClient";
import { mobileNumberValid } from "./utils";

export function isVisitorMobileOtpEnabled() {
  return VISITOR_MOBILE_OTP_ENABLED;
}

export function isPropertyGreMobileOtpEnabled(property) {
  if (!property) {
    return false;
  }
  return Boolean(property.gre_mobile_otp_enabled);
}

export function resolveGreMobileOtpEnabled(propertyId, propertyPools = []) {
  if (!propertyId) {
    return false;
  }
  for (const pool of propertyPools) {
    const match = pool?.find(
      (item) =>
        String(item?.property_id ?? item?._id ?? "") === String(propertyId),
    );
    if (match) {
      return isPropertyGreMobileOtpEnabled(match);
    }
  }
  return false;
}

/** When a GRE has exactly one allocated property, use it before manual dropdown selection. */
export function resolveGreDefaultProperty(propertyPools = []) {
  for (const pool of propertyPools) {
    if (!Array.isArray(pool) || pool.length !== 1) {
      continue;
    }
    const item = pool[0];
    const propertyId = String(item?.property_id ?? item?._id ?? "").trim();
    if (!propertyId) {
      continue;
    }
    return {
      property_id: propertyId,
      property_title: item?.property_title || "",
      gre_mobile_otp_enabled: Boolean(item?.gre_mobile_otp_enabled),
    };
  }
  return null;
}

export function isGreVisitorOtpRequired(isGreUser, isPropertyOtpEnabled) {
  return Boolean(
    isGreUser && isVisitorMobileOtpEnabled() && isPropertyOtpEnabled,
  );
}

export function isGreAddVisitorOtpRequired(
  isGreUser,
  isEditMode,
  isPropertyOtpEnabled,
) {
  return isGreVisitorOtpRequired(isGreUser, isPropertyOtpEnabled);
}

export const initialVisitorMobileOtpState = {
  mobileVerified: false,
  verifiedMobile: "",
  otpSent: false,
  otp: "",
  sendingOtp: false,
  verifyingOtp: false,
};

export function isGreMobileCurrentlyVerified(mobile, otpState) {
  const digits = String(mobile || "").trim();
  return Boolean(
    otpState?.verifiedMobile &&
      digits &&
      digits === otpState.verifiedMobile &&
      otpState.mobileVerified,
  );
}

/** True when OTP state or persisted form flag says this mobile is verified. */
export function isGreMobileVerificationSatisfied(
  mobile,
  otpState,
  visitConfirmationStatus,
) {
  const digits = String(mobile || "").trim();
  return Boolean(
    isGreMobileCurrentlyVerified(digits, otpState) ||
      (isVisitorMobileValid(digits) && Number(visitConfirmationStatus) === 1),
  );
}

export function buildVerifiedGreMobileOtpState(mobile) {
  const digits = String(mobile || "").trim();
  return {
    mobileVerified: true,
    verifiedMobile: digits,
    otpSent: false,
    otp: "",
    sendingOtp: false,
    verifyingOtp: false,
  };
}

export function resolveGreMobileVerificationSnapshot(
  mobile,
  otpState,
  visitConfirmationStatus,
) {
  const isVerified = isGreMobileVerificationSatisfied(
    mobile,
    otpState,
    visitConfirmationStatus,
  );

  return {
    isVerified,
    visitConfirmationStatus: isVerified ? 1 : "",
    otpState: isVerified ? buildVerifiedGreMobileOtpState(mobile) : null,
  };
}

/** Keep visit_confirmation_status when non-mobile fields change (e.g. property, lead source). */
export function greVisitConfirmationStatusForMobile(
  mobile,
  otpState,
  visitConfirmationStatus,
) {
  return isGreMobileVerificationSatisfied(mobile, otpState, visitConfirmationStatus)
    ? 1
    : "";
}

export function shouldShowGreSendOtp(mobile, otpState) {
  const digits = String(mobile || "").trim();
  if (!isVisitorMobileValid(digits)) {
    return false;
  }
  if (otpState?.otpSent) {
    return false;
  }
  return digits !== String(otpState?.verifiedMobile || "").trim();
}

export function isVisitorMobileValid(mobile, countryCode = "+91") {
  if (!mobile) {
    return false;
  }
  if (countryCode === "+91") {
    return mobileNumberValid(mobile);
  }
  return String(mobile).length >= 10;
}

function visitorOtpRequestPayload(payload) {
  return {
    ...payload,
    suppressGlobalLoader: true,
  };
}

export async function sendVisitorMobileOtp(
  mobile,
  countryCode = "+91",
  propertyTitle = "",
  propertyId = "",
) {
  return apiCall(
    "post",
    apiEndPoints.SEND_VISITOR_MOBILE_OTP,
    visitorOtpRequestPayload({
      mobile,
      country_code: countryCode,
      property_title: propertyTitle || "",
      property_id: propertyId || "",
    }),
  );
}

export async function verifyVisitorMobileOtp(
  mobile,
  countryCode = "+91",
  otp,
  propertyId = "",
) {
  return apiCall(
    "post",
    apiEndPoints.VERIFY_VISITOR_MOBILE_OTP,
    visitorOtpRequestPayload({
      mobile,
      country_code: countryCode,
      otp,
      property_id: propertyId || "",
    }),
  );
}

export async function resendVisitorMobileOtp(
  mobile,
  countryCode = "+91",
  propertyTitle = "",
  propertyId = "",
) {
  return apiCall(
    "post",
    apiEndPoints.RESEND_VISITOR_MOBILE_OTP,
    visitorOtpRequestPayload({
      mobile,
      country_code: countryCode,
      property_title: propertyTitle || "",
      property_id: propertyId || "",
    }),
  );
}
